import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { StepPill } from "@/components/onboarding/StepPill";
import { DatasetStep } from "@/components/onboarding/DatasetStep";
import { IdVerificationStep } from "@/components/onboarding/IdVerificationStep";
import { MetricsStep } from "@/components/onboarding/MetricsStep";
import { UNDERTONES, TEXTURES, LENGTHS, SHAPES } from "@/components/onboarding/metricsOptions";
import { ArrowLeft, Camera, CheckCircle2, Eye, IdCard, Ruler } from "lucide-react";

export default function Onboarding() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [model, setModel] = useState(null);
  const [ob, setOb] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(null);
  const [uploadingSlot, setUploadingSlot] = useState(null);
  const [verifyingId, setVerifyingId] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [metricsForm, setMetricsForm] = useState(null);
  const slotFileRef = useRef(null);
  const pendingSlotRef = useRef(null);
  const idFileRef = useRef(null);

  const applyState = useCallback((data, autoStep = false) => {
    setOb(data.onboarding);
    setChecklist(data.checklist);
    if (data.slots) setSlots(data.slots);
    if (autoStep) {
      const c = data.checklist;
      if (!c.dataset_done) setStep(1);
      else if (c.id_status !== "verified") setStep(2);
      else setStep(3);
    }
  }, []);

  const load = useCallback(async () => {
    try {
      const [obRes, modelRes] = await Promise.all([
        api.get(`/models/${id}/onboarding`),
        api.get(`/models/${id}`),
      ]);
      setModel(modelRes.data);
      applyState(obRes.data, true);
    } catch (e) {
      toast.error(errMsg(e, "Could not load onboarding"));
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  }, [id, applyState, navigate]);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  // Prefill metrics form when extraction result arrives
  useEffect(() => {
    const ex = ob?.metadata?.extracted;
    if (ex && !metricsForm) {
      setMetricsForm({
        height_cm: model?.features?.height_cm ?? "",
        skin_undertone: UNDERTONES.includes(ex.skin_undertone) ? ex.skin_undertone : "neutral",
        hair_texture: TEXTURES.includes(ex.hair_texture) ? ex.hair_texture : "straight",
        hair_length: LENGTHS.includes(ex.hair_length) ? ex.hair_length : "medium",
        hair_color: ex.hair_color || "",
        eye_color: ex.eye_color || "",
        body_shape: SHAPES.some((s) => s.value === ex.body_shape) ? ex.body_shape : "rectangle",
      });
    }
  }, [ob, metricsForm, model]);

  const pickSlotFile = (slotKey) => {
    pendingSlotRef.current = slotKey;
    slotFileRef.current?.click();
  };

  const uploadSlotFile = async (files) => {
    const slotKey = pendingSlotRef.current;
    if (!files?.length || !slotKey) return;
    setUploadingSlot(slotKey);
    try {
      const fd = new FormData();
      fd.append("slot", slotKey);
      fd.append("file", files[0]);
      const { data } = await api.post(`/models/${id}/dataset`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      applyState(data);
      toast.success(`${slots.find((s) => s.key === slotKey)?.label || "Photo"} uploaded`);
      if (data.checklist.dataset_done && !checklist.dataset_done) {
        toast.success("Photo dataset complete — continue to ID verification");
      }
    } catch (e) {
      toast.error(errMsg(e, "Upload failed"));
    } finally {
      setUploadingSlot(null);
      if (slotFileRef.current) slotFileRef.current.value = "";
      pendingSlotRef.current = null;
    }
  };

  const removeSlotPhoto = async (photo) => {
    try {
      const { data } = await api.delete(`/models/${id}/dataset/${photo.id}`);
      applyState(data);
      toast.success("Photo removed");
    } catch (e) {
      toast.error(errMsg(e, "Could not remove photo"));
    }
  };

  const uploadIdDoc = async (files) => {
    if (!files?.length) return;
    setVerifyingId(true);
    try {
      const fd = new FormData();
      fd.append("file", files[0]);
      const { data } = await api.post(`/models/${id}/onboarding/verify-id`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      applyState(data);
      const st = data.checklist.id_status;
      if (st === "verified") {
        toast.success("Government ID verified — continue to AI metrics");
        setStep(3);
      } else if (st === "manual_review") {
        toast.error("3 failed attempts — your verification moved to manual review");
      } else {
        toast.error("ID verification did not pass — see the result and retry");
      }
    } catch (e) {
      toast.error(errMsg(e, "ID verification failed. Try again."));
    } finally {
      setVerifyingId(false);
      if (idFileRef.current) idFileRef.current.value = "";
    }
  };

  const runExtraction = async () => {
    setExtracting(true);
    try {
      const { data } = await api.post(`/models/${id}/onboarding/extract-metadata`);
      applyState(data);
      setMetricsForm(null); // re-prefill from fresh extraction
      toast.success("AI analysis complete — review and confirm your metrics");
    } catch (e) {
      toast.error(errMsg(e, "AI analysis failed. Try again."));
    } finally {
      setExtracting(false);
    }
  };

  const confirmMetrics = async () => {
    if (!metricsForm) return;
    setConfirming(true);
    try {
      const payload = {
        ...metricsForm,
        height_cm: metricsForm.height_cm ? parseInt(metricsForm.height_cm) : null,
      };
      const { data } = await api.post(`/models/${id}/onboarding/confirm-metadata`, payload);
      applyState(data);
      if (data.checklist.complete) {
        toast.success("Onboarding complete — your profile is now live in the marketplace");
      } else {
        toast.success("Metrics confirmed — finish the remaining steps to go live");
      }
    } catch (e) {
      toast.error(errMsg(e, "Could not confirm metrics"));
    } finally {
      setConfirming(false);
    }
  };

  if (!authLoading && (!user || !["model", "agency"].includes(user.role))) {
    return (
      <div className="page-container py-20 text-center">
        <p className="font-display text-2xl">Models & agencies only</p>
        <Button className="mt-4 rounded-xl" onClick={() => navigate("/auth")}>Sign in</Button>
      </div>
    );
  }

  if (loading || !checklist) {
    return (
      <div className="page-container max-w-4xl space-y-6 py-10">
        <Skeleton className="h-10 w-72" />
        <div className="grid grid-cols-3 gap-3">
          {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16 rounded-xl" />)}
        </div>
        <Skeleton className="h-96 rounded-2xl" />
      </div>
    );
  }

  const photosBySlot = {};
  (ob?.dataset?.photos || []).forEach((p) => { photosBySlot[p.slot] = p; });
  const requiredSlots = slots.filter((s) => s.required);
  const extraSlots = slots.filter((s) => !s.required);
  const faceSlotKeys = slots.filter((s) => s.group === "face").map((s) => s.key);
  const facePhotoCount = faceSlotKeys.filter((k) => photosBySlot[k]).length;
  const idStatus = checklist.id_status;
  const overallPct = Math.round(
    (checklist.dataset_required_filled / checklist.dataset_required_total) * 50 +
    (idStatus === "verified" ? 20 : 0) +
    (checklist.metadata_confirmed ? 20 : 0) +
    (checklist.portfolio_done ? 10 : 0)
  );
  const preLiveDone = checklist.dataset_done && idStatus === "verified" && checklist.metadata_confirmed;

  const stepState = (n) => {
    const doneMap = {
      1: checklist.dataset_done,
      2: idStatus === "verified",
      3: checklist.metadata_confirmed,
    };
    if (doneMap[n]) return step === n ? "active" : "done";
    if (n === 3 && !checklist.dataset_done) return "locked";
    return step === n ? "active" : "idle";
  };

  return (
    <div className="page-container max-w-4xl py-8 sm:py-12">
      <button data-testid="onboarding-back-button" onClick={() => navigate("/dashboard")}
        className="mb-6 flex items-center gap-1.5 text-sm text-muted-foreground transition-[color] hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Dashboard
      </button>

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Strict onboarding</p>
          <h1 className="font-display text-3xl tracking-[-0.01em] sm:text-4xl">{model?.name || "Model"} — go live checklist</h1>
          <p className="mt-1 max-w-xl text-sm text-muted-foreground">
            To protect real people from likeness theft, every model must pass a government ID scan and
            provide three standardized reference photos (plus a portfolio photo) before appearing in the marketplace.
          </p>
        </div>
        <Badge data-testid="onboarding-overall-progress" variant="secondary" className="rounded-full px-3 py-1 font-mono-plex text-xs">
          {overallPct}% complete
        </Badge>
      </div>

      <Progress data-testid="onboarding-progress-bar" value={overallPct} className="mt-4 h-2" />

      {/* PORTFOLIO REQUIREMENT (managed on the edit page) */}
      {!checklist.complete && !checklist.portfolio_done && (
        <div data-testid="onboarding-portfolio-notice" className={`mt-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border p-4 text-sm ${
          preLiveDone ? "border-2 border-[hsl(43,74%,70%)] bg-[hsl(43,74%,95%)]" : "bg-muted/40"
        }`}>
          <p className={preLiveDone ? "text-[hsl(43,60%,22%)]" : "text-muted-foreground"}>
            <span className="font-semibold">
              {preLiveDone ? "Almost live — one thing left:" : "Also required to go live:"}
            </span>{" "}
            at least one portfolio photo on your profile ({checklist.portfolio_count} uploaded).
          </p>
          <Button data-testid="onboarding-portfolio-button" size="sm" variant="outline"
            className="rounded-xl border bg-card hover:bg-muted"
            onClick={() => navigate(`/models/${id}/edit`)}>
            Add portfolio photos
          </Button>
        </div>
      )}

      {/* COMPLETION BANNER */}
      {checklist.complete && (
        <Card data-testid="onboarding-complete-banner" className="mt-6 rounded-2xl border-2 border-[hsl(174,38%,70%)] bg-[hsl(174,38%,96%)]">
          <CardContent className="flex flex-wrap items-center justify-between gap-4 p-5">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-[hsl(174,38%,34%)]" />
              <div>
                <p className="font-display text-lg text-[hsl(174,45%,18%)]">Onboarding complete — you're live</p>
                <p className="text-sm text-muted-foreground">
                  {model?.name} is now visible in the marketplace and can receive booking requests.
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button data-testid="onboarding-view-profile-button" className="rounded-xl" onClick={() => navigate(`/models/${id}`)}>
                <Eye className="mr-1.5 h-4 w-4" /> View public profile
              </Button>
              <Button variant="outline" className="rounded-xl border bg-card hover:bg-muted" onClick={() => navigate(`/models/${id}/edit`)}>
                Edit profile
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* STEPPER */}
      <div className="mt-6 flex flex-col gap-2 sm:flex-row">
        <StepPill n={1} label="Photo dataset" icon={Camera} state={stepState(1)} onClick={() => setStep(1)} testId="onboarding-step-1-tab" />
        <StepPill n={2} label="Government ID" icon={IdCard} state={stepState(2)} onClick={() => setStep(2)} testId="onboarding-step-2-tab" />
        <StepPill n={3} label="AI metrics" icon={Ruler} state={stepState(3)} onClick={() => checklist.dataset_done && setStep(3)} testId="onboarding-step-3-tab" />
      </div>

      {step === 1 && (
        <DatasetStep
          checklist={checklist}
          requiredSlots={requiredSlots}
          extraSlots={extraSlots}
          photosBySlot={photosBySlot}
          uploadingSlot={uploadingSlot}
          facePhotoCount={facePhotoCount}
          slotFileRef={slotFileRef}
          onPickSlot={pickSlotFile}
          onSlotFileChange={uploadSlotFile}
          onRemovePhoto={removeSlotPhoto}
          onContinue={() => setStep(2)}
        />
      )}

      {step === 2 && (
        <IdVerificationStep
          checklist={checklist}
          ob={ob}
          facePhotoCount={facePhotoCount}
          verifyingId={verifyingId}
          idFileRef={idFileRef}
          onUploadIdDoc={uploadIdDoc}
          setStep={setStep}
        />
      )}

      {step === 3 && (
        <MetricsStep
          ob={ob}
          checklist={checklist}
          extracting={extracting}
          confirming={confirming}
          metricsForm={metricsForm}
          setMetricsForm={setMetricsForm}
          onExtract={runExtraction}
          onConfirm={confirmMetrics}
          setStep={setStep}
        />
      )}

      {/* ETHICS FOOTNOTE */}
      <p className="mt-6 text-[11px] leading-relaxed text-muted-foreground">
        Until every step is complete, your profile is fully hidden from the marketplace, search, and booking —
        clients cannot see or request you. Your ID document lives in private storage and is never served publicly.
      </p>
    </div>
  );
}
